import React, { useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  CAD_UPLOAD_ACCEPT,
  cadUploadRequirementLines,
  formatCadUploadSize,
} from '../../utils/cadUpload';
import {
  busyTextSx,
  dropHintSx,
  dropIconSx,
  dropTitleSx,
  dropZoneInnerSx,
  errorTextSx,
  getPlanCardSx,
  planBusyOverlaySx,
  requirementIconSx,
  requirementItemSx,
  requirementsBoxSx,
  sectionLabelSx,
  selectedFileMetaSx,
  selectedFileNameSx,
  selectedFileRowSx,
  selectedFileSlotSx,
} from './CadUploadFlow.styles';

/**
 * Drop de **un** archivo `.dxf`, con los requisitos a la vista antes de soltarlo.
 * La validación fuerte la hace el parse del API; acá se cortan los casos obvios.
 *
 * @param {{
 *   file: File|null,
 *   requirements: object|null,
 *   error: string,
 *   disabled?: boolean,
 *   busy?: boolean,
 *   busyStage?: string,
 *   onSelect: (file: File) => void,
 *   onClear: () => void,
 * }} props
 */
export default function CadFilePicker({
  file,
  requirements,
  error,
  disabled = false,
  busy = false,
  busyStage = '',
  onSelect,
  onClear,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    const dropped = event.dataTransfer?.files?.[0];
    if (dropped) onSelect(dropped);
  };

  return (
    <Box>
      <Typography component="span" sx={sectionLabelSx}>
        Plano del loteo
      </Typography>

      <Box
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled && !file) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        sx={getPlanCardSx(dragging, Boolean(error), Boolean(file))}
      >
        {file ? (
          <Box sx={selectedFileSlotSx}>
            <Box sx={selectedFileRowSx}>
              <DescriptionOutlinedIcon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={selectedFileNameSx}>{file.name}</Typography>
                <Typography sx={selectedFileMetaSx}>{formatCadUploadSize(file.size)}</Typography>
              </Box>
              <Tooltip title="Quitar archivo" placement="top" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={onClear}
                    disabled={disabled}
                    aria-label="Quitar archivo"
                    sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
                  >
                    <CloseIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <Box
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label="Elegir el archivo .dxf del plano"
            aria-disabled={disabled}
            onClick={openPicker}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openPicker();
              }
            }}
            sx={dropZoneInnerSx}
          >
            <CloudUploadOutlinedIcon sx={dropIconSx} />
            <Typography sx={dropTitleSx}>Arrastrá el plano acá</Typography>
            <Typography sx={dropHintSx}>
              O hacé clic para buscarlo. Un solo archivo .dxf, de hasta 15 MB.
            </Typography>
          </Box>
        )}

        {error ? <Typography sx={errorTextSx}>{error}</Typography> : null}

        <Box sx={requirementsBoxSx}>
          <Typography sx={{ ...selectedFileMetaSx, mb: 1, color: '#d4d4d4' }}>
            Qué tiene que cumplir el plano
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {cadUploadRequirementLines(requirements).map((line) => (
              <Box key={line} sx={requirementItemSx}>
                <CheckCircleOutlineIcon sx={requirementIconSx} />
                <span>{line}</span>
              </Box>
            ))}
          </Box>
        </Box>

        {busy ? (
          <Box sx={planBusyOverlaySx} aria-live="polite" aria-busy="true">
            <CircularProgress size={26} sx={{ color: 'primary.main' }} />
            <Typography sx={busyTextSx}>
              {busyStage || 'Procesando el plano…'}
            </Typography>
            <Typography sx={{ ...busyTextSx, color: 'rgba(255,255,255,0.45)', fontSize: '12.5px' }}>
              Puede tardar unos segundos. No cierres el panel.
            </Typography>
            <Box sx={{ width: '70%', maxWidth: 240 }}>
              <LinearProgress sx={{ borderRadius: 99, height: 4 }} />
            </Box>
          </Box>
        ) : null}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept={CAD_UPLOAD_ACCEPT}
        aria-label="Archivo .dxf del plano"
        hidden
        onChange={(event) => {
          const picked = event.target.files?.[0];
          if (picked) onSelect(picked);
          // Permite volver a elegir el mismo archivo tras un error.
          event.target.value = '';
        }}
      />
    </Box>
  );
}
